import type { JsonValue } from "../document/types.ts";
import { cellAreasOf, type CellArea } from "./cell-fields.ts";
import { COLLISION_CELLS_PER_TILE } from "../document/types.ts";
import { reachWindow, zoneCellSource, type LoweredCells, type ReachWindow, type ThingBox } from "./cell-sources.ts";
import { floodRegion } from "./flood-fill.ts";
import { fieldsOfThing, setThingField, type FieldEditHost, type PickOwner } from "./field-edits.ts";
import { cellsValue, type FieldAddress } from "./field-kinds.ts";
import type { MarkerRef } from "./many-props.ts";
import type { ReferenceThing, ReferenceTypes } from "./references.ts";
import { schemaOfThing, selectedAlone, sameThing } from "./field-owner.ts";

/**
 * Edit on map for a cells field (ADR-070's amendment "the other field kinds"): which field of which thing the
 * map paints, view state never saved or undone, and the strokes it writes, each one command on that thing. It
 * runs for one thing at a time, the lone selected prop or marker, on a layer the tools may edit, and is let go
 * once any of that stops holding; starting it ends activation's Edit on map, since one press paints one thing.
 * A field may follow a painted zone instead (ADR-021's amendment of 2026-10-02): its area is then the zone's, read
 * from the store's lowered map, and it is not painted until it goes back to cells of its own. The map paints a
 * field with Zones mode's tools (ADR-070's amendment of 2026-10-02): cells of the map's collision grid, which
 * `paintMapCells` and `fillMapCell` turn into the field's `[column, rowsUp]` cells, clipped to the thing's reach
 * window.
 */

/**
 * What the edits read from the store.
 */
export interface CellFieldHost extends FieldEditHost {
  readonly types: () => ReferenceTypes;
  readonly lowered: () => LoweredCells;
  readonly box: (owner: ReferenceThing) => ThingBox | null;
  readonly markerSelection: () => readonly MarkerRef[];
  readonly isEditable: (layerId: string) => boolean;
  readonly notify: () => void;
  readonly endActivation: () => void;
}

/**
 * The field the map paints: whose, where and its title.
 */
export interface CellFieldEditing {
  readonly owner: ReferenceThing;
  readonly address: FieldAddress;
  readonly title: string;
}

/**
 * Whether two addresses name the same field or row.
 *
 * @param a One address.
 * @param b The other.
 * @returns True when they match.
 */
function sameAddress(a: FieldAddress, b: FieldAddress): boolean {
  return a.field === b.field && a.index === b.index;
}

/**
 * The cells fields' Edit on map.
 */
export class CellFieldEdits {
  private edited: CellFieldEditing | null = null;

  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: CellFieldHost) {}

  /**
   * The field the map paints.
   *
   * @returns It, or null while none is painted.
   */
  public get editing(): CellFieldEditing | null {
    return this.edited;
  }

  /**
   * Whether the map paints a field of a thing.
   *
   * @param owner The thing.
   * @param address The field, or one row of it.
   * @returns True while it does.
   */
  public isEditing(owner: ReferenceThing, address: FieldAddress): boolean {
    return this.edited !== null && sameThing(this.edited.owner, owner) && sameAddress(this.edited.address, address);
  }

  /**
   * Starts painting a thing's area on the map, ending activation's Edit on map.
   *
   * @param owner The thing, which must be selected alone.
   * @param address A cells field, or one row of a list of cells.
   * @returns True when painting started, false when it was refused.
   */
  public startEditing(owner: ReferenceThing, address: FieldAddress): boolean {
    const area = this.areaOf(owner, address);
    if (area === undefined) {
      return false;
    }
    if (!this.isEditing(owner, address)) {
      this.host.endActivation();
      this.edited = { owner, address, title: area.title };
      this.host.notify();
    }
    return true;
  }

  /**
   * Ends painting, notifying only when it was under way.
   */
  public endEditing(): void {
    if (this.edited !== null) {
      this.edited = null;
      this.host.notify();
    }
  }

  /**
   * Lets painting go once its field can no longer be painted, without notifying: the store calls it on the way
   * to notifying its subscribers.
   */
  public release(): void {
    if (this.edited !== null && this.areaOf(this.edited.owner, this.edited.address) === undefined) {
      this.edited = null;
    }
  }

  /**
   * The cells an area holds.
   *
   * @param owner The thing.
   * @param address The field, or one row of it.
   * @returns The cells as `"column,rowsUp"` keys.
   */
  public cellsOf(owner: ReferenceThing, address: FieldAddress): ReadonlySet<string> {
    const area = this.areasOf(owner).find((a) => sameAddress(a.address, address));
    return new Set((area?.cells ?? []).map(([c, r]) => `${c},${r}`));
  }

  /**
   * Every area a thing's fields hold, for the map to draw: an area that follows a zone holds the cells the zone
   * gives it.
   *
   * @param owner The thing.
   * @returns The areas, none for a thing gone or of a type the game does not register.
   */
  public areasOf(owner: ReferenceThing): readonly CellArea[] {
    const schema = schemaOfThing(this.host, owner);
    const fields = fieldsOfThing(this.host.map(), owner);
    if (schema === undefined || fields === undefined) {
      return [];
    }
    const areas = cellAreasOf(schema, fields);
    if (!areas.some((area) => area.zone !== undefined)) {
      return areas;
    }
    const lowered = cellAreasOf(schema, fieldsOfThing(this.host.lowered().map, owner) ?? {});
    return areas.map((area) => (area.zone === undefined ? area : { ...area, cells: lowered.find((l) => sameAddress(l.address, area.address))?.cells ?? [] }));
  }

  /**
   * Makes an area follow a zone, or gives it the cells it follows as cells of its own, as one command; a field
   * that starts following a zone is no longer painted.
   *
   * @param owner The thing.
   * @param address The field, or one row of it.
   * @param zone The zone type to follow, or null for cells of its own.
   * @returns False when nothing changed, the field cannot be edited or the command was refused.
   */
  public follow(owner: ReferenceThing, address: FieldAddress, zone: string | null): boolean {
    const area = this.areaOf(owner, address, false);
    if (area === undefined) {
      return false;
    }
    if (zone !== null) {
      return setThingField(this.host, owner, address, zoneCellSource(zone));
    }
    return area.zone !== undefined && this.write(owner, address, new Set(area.cells.map(([c, r]) => `${c},${r}`)));
  }

  /**
   * Writes a stroke's cells to the painted field as one command.
   *
   * @param cells Every cell the area holds after the stroke.
   * @returns False when nothing is painted, nothing changed or the command was refused.
   */
  public paint(cells: ReadonlySet<string>): boolean {
    const edited = this.edited;
    return edited !== null && this.write(edited.owner, edited.address, cells);
  }

  /**
   * A thing's box on the map in view, which its reach window is grown from.
   *
   * @param owner The thing.
   * @returns The box, or null when the thing is not on the map.
   */
  public boxOf(owner: ReferenceThing): ThingBox | null {
    return this.host.box(owner);
  }

  /**
   * Paints or erases cells of the map's collision grid in the painted field as one command, each cell the
   * thing's `[column, rowsUp]` from its anchor; cells outside the thing's reach window are left out.
   *
   * @param indices Cell indices in the map's collision grid.
   * @param erase True to take the cells out of the area.
   * @returns False when nothing is painted, no cell lies in the window, nothing changed or the command was
   *   refused.
   */
  public paintMapCells(indices: readonly number[], erase: boolean): boolean {
    const target = this.paintTarget();
    if (target === null) {
      return false;
    }
    const { edited, box, window, width } = target;
    const next = new Set(this.cellsOf(edited.owner, edited.address));
    let inside = false;
    for (const index of indices) {
      const x = index % width;
      const y = (index - x) / width;
      if (x < window.left || x > window.right || y < window.top || y > window.bottom) {
        continue;
      }
      inside = true;
      const key = `${x - box.ax},${box.ay - 1 - y}`;
      if (erase) {
        next.delete(key);
      } else {
        next.add(key);
      }
    }
    return inside && this.write(edited.owner, edited.address, next);
  }

  /**
   * Fills from a cell of the map's collision grid in the painted field as one command: the 4-connected run of
   * cells inside the reach window that are out of the area, as the pressed cell is, joins it; erasing, the run
   * that is in it, as the pressed cell is, leaves it.
   *
   * @param index The pressed cell's index in the map's collision grid.
   * @param erase True to erase the run the pressed cell is in.
   * @returns False when nothing is painted, the cell lies outside the window, the pressed cell already holds
   *   what the fill would give it, or the command was refused.
   */
  public fillMapCell(index: number, erase: boolean): boolean {
    const target = this.paintTarget();
    if (target === null) {
      return false;
    }
    const { edited, box, window, width } = target;
    const px = index % width;
    const py = (index - px) / width;
    if (px < window.left || px > window.right || py < window.top || py > window.bottom) {
      return false;
    }
    const wide = window.right - window.left + 1;
    const high = window.bottom - window.top + 1;
    const base = this.cellsOf(edited.owner, edited.address);
    const member = new Uint8Array(wide * high);
    for (const key of base) {
      const [column, rowsUp] = key.split(",").map(Number) as [number, number];
      const x = box.ax + column - window.left;
      const y = box.ay - 1 - rowsUp - window.top;
      if (x >= 0 && x < wide && y >= 0 && y < high) {
        member[y * wide + x] = 1;
      }
    }
    const start = (py - window.top) * wide + (px - window.left);
    if ((member[start] === 1) !== erase) {
      return false;
    }
    const next = new Set(base);
    for (const local of floodRegion(member, wide, high, start)) {
      const x = (local % wide) + window.left;
      const y = Math.floor(local / wide) + window.top;
      const key = `${x - box.ax},${box.ay - 1 - y}`;
      if (erase) {
        next.delete(key);
      } else {
        next.add(key);
      }
    }
    return this.write(edited.owner, edited.address, next);
  }

  /**
   * Empties an area as one command: a field then holds no value, a row an empty area. For every selected prop
   * at once, the field is emptied on each.
   *
   * @param owner The thing, or the selected props.
   * @param address The field, or one row of it.
   * @returns False when nothing changed or the command was refused.
   */
  public clear(owner: PickOwner, address: FieldAddress): boolean {
    if (owner.kind === "props") {
      return address.index === undefined && setThingField(this.host, owner, address, null);
    }
    return this.areaOf(owner, address, false) !== undefined && this.write(owner, address, new Set());
  }

  /**
   * What a map press paints: the field painted, its thing's box and reach window, and the collision grid's width.
   *
   * @returns The target, or null while no field is painted or its thing is gone.
   */
  private paintTarget(): { readonly edited: CellFieldEditing; readonly box: ThingBox; readonly window: ReachWindow; readonly width: number } | null {
    const edited = this.edited;
    const box = edited === null ? null : this.host.box(edited.owner);
    if (edited === null || box === null) {
      return null;
    }
    const map = this.host.map();
    const width = map.width * COLLISION_CELLS_PER_TILE;
    return { edited, box, window: reachWindow(box, width, map.height * COLLISION_CELLS_PER_TILE), width };
  }

  /**
   * Sets an area's cells.
   *
   * @param owner The thing.
   * @param address The field, or one row of it.
   * @param cells The cells.
   * @returns False when nothing changed or the command was refused.
   */
  private write(owner: ReferenceThing, address: FieldAddress, cells: ReadonlySet<string>): boolean {
    const value: JsonValue | null = cellsValue(cells) ?? (address.index === undefined ? null : []);
    return setThingField(this.host, owner, address, value);
  }

  /**
   * The area at an address, when it may be painted: the thing selected alone, its area its own cells rather than
   * a zone's (unless only its editability is asked), on an editable layer, its field a cells field or a row of a
   * list of cells that exists.
   *
   * @param owner The thing.
   * @param address The field, or one row of it.
   * @param painted Whether the area must be one the map paints: the lone selection's, following no zone.
   * @returns The area, or undefined.
   */
  private areaOf(owner: ReferenceThing, address: FieldAddress, painted = true): CellArea | undefined {
    if (!this.host.isEditable(owner.layerId) || (painted && !selectedAlone(this.host, owner))) {
      return undefined;
    }
    const area = this.areasOf(owner).find((a) => sameAddress(a.address, address));
    return painted && area?.zone !== undefined ? undefined : area;
  }
}
